import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Tiny shared cache: one fetch per page load, shared by every component. */
function cached(loader) {
  let data = null;
  let inflight = null;
  const subs = new Set();
  const load = () => {
    if (data) return Promise.resolve(data);
    if (!inflight) {
      inflight = loader()
        .then((d) => { data = d; subs.forEach((f) => f(d)); return d; })
        .catch(() => null)
        .finally(() => { inflight = null; });
    }
    return inflight;
  };
  return function useCached() {
    const [value, setValue] = useState(data);
    useEffect(() => {
      let alive = true;
      const cb = (d) => alive && setValue(d);
      subs.add(cb);
      if (!data) load().then((d) => d && cb(d));
      return () => { alive = false; subs.delete(cb); };
    }, []);
    return value;
  };
}

export const useCatalog = cached(() => api.get("/products").then((r) => r.data.products || []));
export const useOffers = cached(() => api.get("/offers").then((r) => r.data));

export const FALLBACK_FREE_SHIPPING_AT = 998;

/** Match a page's "best jar" hint to a live product and variant. */
export function findBottle(products, bestFor) {
  if (!products?.length || !bestFor) return null;
  const hint = bestFor.toLowerCase();
  if (hint.includes("chintu")) {
    const p = products.find((x) => x.slug === "karijeeva-chintu-edition");
    return p ? { product: p, variant: p.variants?.[0] } : null;
  }
  if (hint.includes("family")) {
    const p = products.find((x) => x.kind === "bundle");
    return p ? { product: p, variant: p.variants?.[0] } : null;
  }
  const p = products.find((x) => x.slug === "cold-pressed-coconut-oil") || products.find((x) => (x.kind || "bottle") === "bottle");
  if (!p) return null;
  const size = hint.replace(/\s/g, "");
  const v = p.variants?.find((x) => x.size.toLowerCase().replace(/\s/g, "") === size) || p.variants?.[0];
  return { product: p, variant: v };
}

/** "2 × 1 L" bundle vs the same number of single 1 L jars. */
export function bundleSaving(variant, products) {
  const m = /^(\d+)\s*[×x]\s*(.+)$/.exec(variant?.size || "");
  if (!m) return null;
  const n = Number(m[1]);
  const unit = m[2].toLowerCase().replace(/\s/g, "");
  const single = products
    ?.find((p) => p.slug === "cold-pressed-coconut-oil")
    ?.variants?.find((v) => v.size.toLowerCase().replace(/\s/g, "") === unit);
  if (!single) return null;
  const singles = single.price * n;
  const saving = Math.round(singles - variant.price);
  return saving > 0 ? { singles, saving, pct: Math.round((saving / singles) * 100), perBottle: Math.round(variant.price / n) } : null;
}

export function pricePerLitre(variant) {
  const m = /^(?:(\d+)\s*[×x]\s*)?(\d+(?:\.\d+)?)\s*(ml|l)$/i.exec((variant?.size || "").trim());
  if (!m) return null;
  const n = Number(m[1] || 1);
  const litres = n * Number(m[2]) * (m[3].toLowerCase() === "ml" ? 0.001 : 1);
  return litres ? Math.round(variant.price / litres) : null;
}

export const isSoldOut = (product) => !!product?.variants?.length && product.variants.every((v) => (v.stock ?? 0) <= 0);
export const preorderHref = (variant) => `/pre-order${variant?.id ? `?variant=${variant.id}` : ""}`;

/** Real pre-order total, fetched fresh each time (never simulated). */
export function usePreorderSummary() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/preorders/summary").then((r) => alive && setData(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);
  return [data, setData];
}
